/**
 * avatarStorageService — single source of truth for avatar storage I/O.
 *
 * Storage model:
 *   - The `avatars` bucket is PRIVATE.
 *   - `profiles.avatar_url` stores a relative storage path: `<uid>/avatar.<ext>`.
 *   - Read access is via short-lived signed URLs only.
 *   - The signing endpoint is reachable with the anon key (no user session
 *     required), so signed URLs work for both anonymous visitors and signed-in
 *     users.
 *
 * UI must NOT import this file directly — use the `@/services/profile` barrel
 * (or the `<UserAvatar>` component which delegates here).
 *
 * Performance:
 *   - Per-path in-memory cache (positive + negative).
 *   - Cross-page persistence via `sessionStorage` (TTL-bounded), so navigating
 *     between routes does not re-sign URLs that are still valid.
 *   - Microtask-batching: concurrent `resolveAvatarUrl()` calls within the same
 *     tick are coalesced into a single `createSignedUrls()` HTTP request.
 *     A list of N avatars produces 1 storage call instead of N.
 */
import { supabase } from "@/integrations/supabase/client";

import { isValidAvatarPath } from "@/lib/security/storagePaths";
const BUCKET = "avatars";
const AVATAR_EXTENSIONS = ["jpg", "jpeg", "png", "webp"] as const;

/** Signed URL TTL in seconds (55 min). Cached entries are invalidated 5 min earlier. */
const SIGNED_URL_TTL_SECONDS = 55 * 60;
const CACHE_TTL_MS = (SIGNED_URL_TTL_SECONDS - 5 * 60) * 1000;
/**
 * Negative cache TTL — keep just long enough to coalesce a single render burst,
 * but short enough that an upload completed shortly after an earlier failed
 * lookup is picked up almost immediately. A previous 60s TTL caused freshly
 * uploaded avatars to remain hidden until the negative entry expired.
 */
const NEGATIVE_CACHE_TTL_MS = 3 * 1000;
/**
 * Post-upload protection window. When an avatar upload succeeds we mark the
 * returned path (and its sibling extensions) as "recently uploaded" for this
 * many milliseconds. Within the window:
 *   - Negative cache writes for those paths are REFUSED. A racing in-flight
 *     resolve that started before the upload (and ends up returning `null`
 *     because storage hadn't propagated yet) cannot poison the cache and
 *     hide the freshly uploaded image.
 *   - Stale invalidate/peek cycles cannot push a `null` entry that would
 *     suppress the just-uploaded asset until the negative TTL elapses.
 * 30s is generous enough to cover slow CDN propagation while staying short
 * enough that genuinely missing files become discoverable again quickly.
 */
const UPLOAD_PROTECTION_WINDOW_MS = 30 * 1000;
/** Storage key prefix for sessionStorage persistence. */
const SESSION_STORAGE_PREFIX = "scuvio.avatar.signed.v1.";
/** Max paths per single createSignedUrls() call. */
const BATCH_MAX = 100;

/** Path shape we accept: `<uuid>/avatar.<ext>`. */
const AVATAR_PATH_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/avatar\.(jpg|jpeg|png|webp)$/i;

interface CacheEntry {
  /** Resolved signed URL, or `null` for a cached negative result. */
  url: string | null;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();

/**
 * Map of `path → expiresAt` for paths protected from negative caching after
 * a confirmed successful upload. Entries are cleaned lazily on access.
 */
const uploadProtectedUntil = new Map<string, number>();

function isUploadProtected(path: string): boolean {
  const until = uploadProtectedUntil.get(path);
  if (!until) return false;
  if (until <= Date.now()) {
    uploadProtectedUntil.delete(path);
    return false;
  }
  return true;
}

export function isAvatarPathUploadProtected(
  value: string | null | undefined,
): boolean {
  const path = normalizeAvatarPath(value);
  if (!path) return false;
  return isUploadProtected(path);
}

/**
 * Mark a path (and its sibling extensions for the same user) as protected
 * from negative caching for the upload protection window. Called from the
 * upload flow once the server has acknowledged a successful write.
 */
function protectPathAfterUpload(path: string): void {
  const expiresAt = Date.now() + UPLOAD_PROTECTION_WINDOW_MS;
  uploadProtectedUntil.set(path, expiresAt);
  // Protect sibling extensions too — the resolver may be looking at the
  // previously-bound `<uid>/avatar.jpg` while the new file landed at
 // `<uid>/avatar.webp`. We don't want a `null` for the old extension to
  // be cached either, since the UI may briefly query it before the parent
  // profile prop catches up to the new path.
  const match = path.match(/^(.+)\/avatar\.(jpg|jpeg|png|webp)$/i);
  if (match) {
    const prefix = match[1];
    for (const ext of AVATAR_EXTENSIONS) {
      uploadProtectedUntil.set(`${prefix}/avatar.${ext}`, expiresAt);
    }
  }
}

/** Pending lookups awaiting the next batch flush. Map: path → resolvers. */
const pendingResolvers = new Map<string, Array<(url: string | null) => void>>();
let flushScheduled = false;

/** Safe sessionStorage access (SSR / private-mode tolerant). */
function getStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readSessionEntry(path: string): CacheEntry | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(SESSION_STORAGE_PREFIX + path);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { url: string | null; expiresAt: number };
    if (
      typeof parsed?.expiresAt !== "number" ||
      (parsed.url !== null && typeof parsed.url !== "string")
    ) {
      return null;
    }
    return { url: parsed.url, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

function writeSessionEntry(path: string, entry: CacheEntry): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(
      SESSION_STORAGE_PREFIX + path,
      JSON.stringify({ url: entry.url, expiresAt: entry.expiresAt }),
    );
  } catch {
    /* quota exceeded or disabled — silently ignore */
  }
}

function deleteSessionEntry(path: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(SESSION_STORAGE_PREFIX + path);
  } catch {
    /* ignore */
  }
}

/** Read from in-memory cache, hydrating from sessionStorage on first hit. */
function readCache(path: string): CacheEntry | null {
  const now = Date.now();
  const mem = cache.get(path);
  if (mem && mem.expiresAt > now) return mem;
  if (mem) cache.delete(path);

  const session = readSessionEntry(path);
  if (session && session.expiresAt > now) {
    cache.set(path, session);
    return session;
  }
  if (session) deleteSessionEntry(path);
  return null;
}

function writeCache(path: string, entry: CacheEntry): void {
  // Guard: never persist a negative result for a path that was just
  // confirmed-uploaded. A `null` here would otherwise hide the new image
  // until the negative TTL elapses, even though the bytes are present in
  // storage. The resolver returns `null` for THIS call (so the caller can
  // schedule a retry) but the cache stays empty, allowing the very next
  // resolve to hit the network again instead of reading a stale negative.
  if (entry.url === null && isUploadProtected(path)) {
    cache.delete(path);
    deleteSessionEntry(path);
    return;
  }
  cache.set(path, entry);
  // Only persist positive results across navigations. Negative results stay
  // in-memory only so a transient failure cannot survive a page reload and
  // keep an avatar hidden after a successful upload.
  if (entry.url !== null) {
    writeSessionEntry(path, entry);
  } else {
    deleteSessionEntry(path);
  }
}

/**
 * Normalize an `avatar_url` value to a storage path.
 *
 * Accepts either a clean storage path (post-migration) or a legacy full URL
 * (defensive — should not occur after the data migration). Returns `null`
 * if the value cannot be normalized to a valid avatar path.
 */
export function normalizeAvatarPath(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Already a clean path?
  if (AVATAR_PATH_PATTERN.test(trimmed)) return trimmed;

  // Legacy full URL — extract the path segment after `/avatars/`.
  const match = trimmed.match(
    /\/storage\/v1\/object\/(?:public|sign|authenticated)\/avatars\/([^?]+)/i,
  );
  if (match) {
    const path = match[1];
    if (AVATAR_PATH_PATTERN.test(path)) return path;
  }
  return null;
}

/**
 * Schedule a single batch flush at the end of the current microtask tick.
 * All `resolveAvatarUrl()` calls made before the flush share one HTTP request.
 */
function scheduleFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(() => {
    flushScheduled = false;
    void flushPending();
  });
}

async function flushPending(): Promise<void> {
  if (pendingResolvers.size === 0) return;

  // Snapshot + clear the pending map so any new requests start a fresh batch.
  const batch = new Map(pendingResolvers);
  pendingResolvers.clear();

  const paths = Array.from(batch.keys());

  // Chunk if we exceed the batch ceiling.
  for (let i = 0; i < paths.length; i += BATCH_MAX) {
    const chunk = paths.slice(i, i + BATCH_MAX);
    await signChunk(chunk, batch);
  }
}

async function signChunk(
  paths: string[],
  batch: Map<string, Array<(url: string | null) => void>>,
): Promise<void> {
  const settle = (path: string, url: string | null, ttlMs: number) => {
    writeCache(path, { url, expiresAt: Date.now() + ttlMs });
    const resolvers = batch.get(path);
    if (resolvers) {
      for (const r of resolvers) r(url);
      batch.delete(path);
    }
  };

  const signViaFunction = async () => {
    if (typeof supabase.functions?.invoke !== "function") {
      for (const path of paths) settle(path, null, NEGATIVE_CACHE_TTL_MS);
      return;
    }

    const response = await supabase.functions.invoke<{
      ok: boolean;
      results?: Array<{ path: string; signedUrl: string | null; error?: string | null }>;
      code?: string;
      message?: string;
    }>("avatar-sign", {
      body: { paths, expiresIn: SIGNED_URL_TTL_SECONDS },
    });
    const data = response?.data;
    const error = response?.error;

    if (error || !data?.ok || !Array.isArray(data.results)) {
      // eslint-disable-next-line no-console
      console.warn('[avatarStorageService] avatar-sign batch failed', { paths, error, data });
      for (const path of paths) settle(path, null, NEGATIVE_CACHE_TTL_MS);
      return;
    }

    const seen = new Set<string>();
    for (const row of data.results) {
      seen.add(row.path);
      if (row.signedUrl && !row.error) {
        settle(row.path, row.signedUrl, CACHE_TTL_MS);
      } else {
        // eslint-disable-next-line no-console
        console.warn('[avatarStorageService] avatar-sign row failed', { path: row.path, error: row.error, signedUrl: row.signedUrl });
        settle(row.path, null, NEGATIVE_CACHE_TTL_MS);
      }
    }

    for (const path of paths) {
      if (!seen.has(path)) settle(path, null, NEGATIVE_CACHE_TTL_MS);
    }
  };

  // Defense-in-depth: drop any path that does not match the strict avatar
  // pattern before issuing storage I/O. Service-role validation in
  // `avatar-sign` is authoritative, but we filter here so the SDK call cannot
  // be tricked into requesting a traversal path.
  const safePaths = paths.filter(isValidAvatarPath);
  for (const p of paths) {
    if (!safePaths.includes(p)) settle(p, null, NEGATIVE_CACHE_TTL_MS);
  }
  if (safePaths.length === 0) return;

  try {
    for (const path of safePaths) {
      if (path.includes('..')) {
        throw new Error('Invalid path');
      }
    }
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(safePaths, SIGNED_URL_TTL_SECONDS);

    if (error || !data) {
      // eslint-disable-next-line no-console
      console.warn('[avatarStorageService] createSignedUrls batch failed, falling back to avatar-sign', { paths, error });
      await signViaFunction();
      return;
    }

    const seen = new Set<string>();
    for (const row of data) {
      seen.add(row.path);
      if (row.signedUrl && !row.error) {
        settle(row.path, row.signedUrl, CACHE_TTL_MS);
      } else {
        // eslint-disable-next-line no-console
        console.warn('[avatarStorageService] sign row failed', { path: row.path, error: row.error, signedUrl: row.signedUrl });
        settle(row.path, null, NEGATIVE_CACHE_TTL_MS);
      }
    }
    // Defensive: any path not present in the response gets negative-cached.
    for (const path of paths) {
      if (!seen.has(path)) settle(path, null, NEGATIVE_CACHE_TTL_MS);
    }
  } catch {
    await signViaFunction();
  }
}

/**
 * Resolve an `avatar_url` value (path or legacy URL) to a short-lived signed URL.
 *
 * Returns `null` when the input is empty, malformed, or signing fails.
 *
 * Optimizations:
 *   - In-memory + sessionStorage cache for both successes and failures.
 *   - All concurrent calls within a microtask tick are batched into a single
 *     `createSignedUrls()` HTTP request (N callers → 1 round-trip).
 *   - Identical pending paths share a single resolver list — no duplicate work.
 */
export async function resolveAvatarUrl(
  value: string | null | undefined,
): Promise<string | null> {
  const path = normalizeAvatarPath(value);
  if (!path) return null;

  const cached = readCache(path);
  if (cached) return cached.url;

  return new Promise<string | null>((resolve) => {
    const existing = pendingResolvers.get(path);
    if (existing) {
      existing.push(resolve);
      return;
    }
    pendingResolvers.set(path, [resolve]);
    scheduleFlush();
  });
}

/**
 * Synchronous probe of the avatar cache. Returns the cached signed URL when
 * fresh, or `null` when no usable entry exists. Never triggers a network
 * request — useful for render-time decisions (e.g. avoiding a loading
 * skeleton on cache hits).
 */
export function peekAvatarUrl(
  value: string | null | undefined,
): string | null {
  const path = normalizeAvatarPath(value);
  if (!path) return null;
  const cached = readCache(path);
  return cached ? cached.url : null;
}

/**
 * Invalidate the cached signed URL for a given avatar path.
 * Call this after upload/replace so subsequent reads pick up the new file.
 *
 * Also broadcasts a window-level event so mounted `<UserAvatar>` instances
 * displaying the same path can re-resolve immediately — important for the
 * "replace avatar" case where the storage path is unchanged
 * (e.g. `<uid>/avatar.jpg` → same `<uid>/avatar.jpg`) and would otherwise
 * keep showing the prior image cached in component state / browser cache.
 */
export function invalidateAvatarCache(value: string | null | undefined): void {
  const path = normalizeAvatarPath(value);
  if (!path) return;
  cache.delete(path);
  deleteSessionEntry(path);
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent(AVATAR_INVALIDATED_EVENT, { detail: { path } }),
      );
    } catch {
      /* noop — best effort */
    }
  }
}

/** Window event fired when an avatar path's cache is invalidated. */
export const AVATAR_INVALIDATED_EVENT = "scuvio:avatar-invalidated";

/**
 * Upload (or replace) the avatar JPEG for the given user.
 * Returns the storage path (`<uid>/avatar.jpg`) — NOT a URL.
 * Persist this path in `profiles.avatar_url`.
 */
export async function uploadAvatarFile(
  userId: string,
  blob: Blob,
): Promise<string> {
  // We send JSON+base64 instead of multipart/form-data because
  // `supabase.functions.invoke` does not reliably preserve the multipart
  // boundary on all browsers (the SDK injects a JSON content-type when
  // the body is read as a clone), which caused uploads to silently fail
  // with a 415/400 before ever reaching server-side validation.
  const mime =
    blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
  const dataBase64 = await blobToBase64(blob);

  const { data, error } = await supabase.functions.invoke<{
    ok: boolean;
    path?: string;
    code?: string;
    message?: string;
    details?: unknown;
  }>("avatar-upload", {
    body: { mime, data_base64: dataBase64 },
  });

  if (error) {
    const ctx = (error as { context?: { body?: unknown } }).context?.body;
    const parsed = parseFunctionErrorBody(ctx);
    throw new AvatarUploadError(
      parsed?.code ?? "INTERNAL_ERROR",
      parsed?.message ?? error.message,
      parsed?.details,
    );
  }
  if (!data?.ok || !data.path) {
    throw new AvatarUploadError(
      (data?.code as AvatarUploadErrorCode) ?? "INTERNAL_ERROR",
      data?.message ?? "Avatar upload failed.",
      data?.details,
    );
  }

  // Protect this path (and sibling extensions) from negative caching for the
  // protection window. Must happen BEFORE the invalidate calls below so any
  // immediately-following resolve that returns `null` (CDN not yet warm)
  // cannot poison the cache for the freshly uploaded asset.
  protectPathAfterUpload(data.path);

  // Invalidate any cached signed URL for this user across all supported
  // extensions — critical when the user replaces their avatar with a
  // different format (e.g. previous `.jpeg`, new `.webp`) so stale URLs /
  // negative entries for the old extension don't keep displaying.
  for (const ext of AVATAR_EXTENSIONS) {
    invalidateAvatarCache(`${userId}/avatar.${ext}`);
  }

  // Pre-warm the cache for the freshly uploaded path so the next render
  // (after `setF({ avatar_url })`) is a synchronous cache hit — no skeleton,
  // no second round-trip, the new image appears immediately.
  // Best effort: failures are swallowed; the UserAvatar will still resolve
  // lazily on render if pre-warming hits a transient error.
  try {
    const signedUrl = await resolveAvatarUrl(data.path);
    // Re-broadcast the invalidation event with the actual returned path.
    // Mounted `<UserAvatar>` instances bound to this exact path (e.g.
    // navbar/avatar in another part of the tree) will pick it up and
    // re-render with the freshly resolved URL — no page refresh needed.
    if (signedUrl && typeof window !== "undefined") {
      try {
        window.dispatchEvent(
          new CustomEvent(AVATAR_INVALIDATED_EVENT, {
            detail: { path: data.path },
          }),
        );
      } catch {
        /* noop */
      }
    }
  } catch {
    /* pre-warm is best-effort; ignore */
  }

  return data.path;
}

/** Encode a Blob to a plain base64 string (no `data:` prefix). */

/**
 * Delete the current user's avatar from storage and clear `profiles.avatar_url`.
 * Server-side (service role) so all extension variants are removed atomically.
 */
export async function deleteAvatarFile(userId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke<{
    ok: boolean;
    code?: string;
    message?: string;
  }>("avatar-upload", {
    method: "DELETE",
  });

  if (error) {
    const ctx = (error as { context?: { body?: unknown } }).context?.body;
    const parsed = parseFunctionErrorBody(ctx);
    throw new AvatarUploadError(
      (parsed?.code as AvatarUploadErrorCode) ?? "INTERNAL_ERROR",
      parsed?.message ?? error.message,
    );
  }
  if (!data?.ok) {
    throw new AvatarUploadError(
      (data?.code as AvatarUploadErrorCode) ?? "INTERNAL_ERROR",
      data?.message ?? "Avatar delete failed.",
    );
  }

  // Drop all cached entries for this user across supported extensions so the
  // UI immediately falls back to initials instead of a stale signed URL.
  for (const ext of AVATAR_EXTENSIONS) {
    invalidateAvatarCache(`${userId}/avatar.${ext}`);
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  // Chunk to avoid "Maximum call stack size exceeded" on large payloads.
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < buf.length; i += CHUNK) {
    binary += String.fromCharCode(...buf.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/* ------------------------------------------------------------------ */
/* Typed upload error                                                   */
/* ------------------------------------------------------------------ */

export type AvatarUploadErrorCode =
  | "AUTH_REQUIRED"
  | "BAD_REQUEST"
  | "FILE_TOO_LARGE"
  | "FILE_TOO_SMALL"
  | "INVALID_MIME"
  | "MIME_MISMATCH"
  | "IMAGE_DECODE_FAILED"
  | "IMAGE_TOO_SMALL"
  | "IMAGE_TOO_LARGE"
  | "INTERNAL_ERROR";

export class AvatarUploadError extends Error {
  readonly code: AvatarUploadErrorCode;
  readonly details?: unknown;
  constructor(code: AvatarUploadErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = "AvatarUploadError";
    this.code = code;
    this.details = details;
  }
}

function parseFunctionErrorBody(
  body: unknown,
): { code?: AvatarUploadErrorCode; message?: string; details?: unknown } | null {
  if (body == null) return null;
  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body) as Record<string, unknown>;
      return {
        code: parsed.code as AvatarUploadErrorCode | undefined,
        message: typeof parsed.message === "string" ? parsed.message : undefined,
        details: parsed.details,
      };
    } catch {
      return null;
    }
  }
  if (typeof body === "object") {
    const b = body as Record<string, unknown>;
    return {
      code: b.code as AvatarUploadErrorCode | undefined,
      message: typeof b.message === "string" ? b.message : undefined,
      details: b.details,
    };
  }
  return null;
}

/** Test-only: clear the in-memory + sessionStorage caches and pending queue. */
export function __resetAvatarCacheForTests(): void {
  cache.clear();
  pendingResolvers.clear();
  flushScheduled = false;
  uploadProtectedUntil.clear();
  const storage = getStorage();
  if (storage) {
    try {
      const keysToDelete: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(SESSION_STORAGE_PREFIX)) keysToDelete.push(key);
      }
      for (const k of keysToDelete) storage.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}